import React, { useState, useEffect } from 'react';
import { 
  Book, ChevronDown, ChevronRight, Search, Home, MessageSquare, 
  FileText, ExternalLink, Calendar, User, Tag, ArrowLeft
} from 'lucide-react';

const KnowledgeCenter = () => {
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [expandedTopics, setExpandedTopics] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch articles from backend
  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await fetch('http://localhost:8000/api/knowledge/articles');
      const data = await response.json();
      setArticles(data.articles || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setLoading(false);
    }
  };

  // Knowledge structure with topics and subtopics
  const knowledgeStructure = [
    {
      id: 'vat-basics',
      title: 'VAT Basics',
      icon: '📚',
      subtopics: [
        { id: 'what-is-vat', title: 'What is VAT?', slug: 'what-is-vat' },
        { id: 'who-pays-vat', title: 'Who Pays VAT?', slug: 'who-pays-vat' },
        { id: 'vat-vs-sales-tax', title: 'VAT vs Sales Tax', slug: 'vat-vs-sales-tax' },
      ]
    },
    {
      id: 'registration',
      title: 'VAT Registration',
      icon: '📝',
      subtopics: [
        { id: 'threshold', title: 'Registration Threshold', slug: 'registration-threshold' },
        { id: 'how-to-register', title: 'How to Register', slug: 'how-to-register' },
        { id: 'voluntary-registration', title: 'Voluntary Registration', slug: 'voluntary-registration' },
        { id: 'deregistration', title: 'Deregistration', slug: 'deregistration' },
      ]
    },
    {
      id: 'rates',
      title: 'VAT Rates & Deductions',
      icon: '💰',
      subtopics: [
        { id: 'standard-rate', title: 'Standard Rate (8.1%)', slug: 'standard-rate' },
        { id: 'reduced-rate', title: 'Reduced Rate (2.6%)', slug: 'reduced-rate' },
        { id: 'accommodation-rate', title: 'Accommodation Rate (3.8%)', slug: 'accommodation-rate' },
        { id: 'input-tax-deduction', title: 'Input Tax Deduction', slug: 'input-tax-deduction' },
        { id: 'exempt-supplies', title: 'Exempt Supplies', slug: 'exempt-supplies' },
      ]
    },
    {
      id: 'industries',
      title: 'Industry-Specific Guides',
      icon: '🏢',
      subtopics: [
        { id: 'e-commerce', title: 'E-commerce & Online Sales', slug: 'e-commerce' },
        { id: 'restaurants', title: 'Restaurants & Catering', slug: 'restaurants' },
        { id: 'real-estate', title: 'Real Estate', slug: 'real-estate' },
        { id: 'healthcare', title: 'Healthcare Services', slug: 'healthcare' },
        { id: 'education', title: 'Education & Training', slug: 'education' },
        { id: 'consulting', title: 'Consulting Services', slug: 'consulting' },
      ]
    },
    {
      id: 'international',
      title: 'International VAT',
      icon: '🌍',
      subtopics: [
        { id: 'cross-border-services', title: 'Cross-Border Services', slug: 'cross-border-services' },
        { id: 'reverse-charge', title: 'Reverse Charge Mechanism', slug: 'reverse-charge' },
        { id: 'exports', title: 'Exports & Zero-Rating', slug: 'exports' },
        { id: 'imports', title: 'Import VAT', slug: 'imports' },
      ]
    },
    {
      id: 'compliance',
      title: 'Compliance & Reporting',
      icon: '📊',
      subtopics: [
        { id: 'filing-returns', title: 'Filing VAT Returns', slug: 'filing-returns' },
        { id: 'record-keeping', title: 'Record Keeping', slug: 'record-keeping' },
        { id: 'invoicing-requirements', title: 'Invoicing Requirements', slug: 'invoicing-requirements' },
        { id: 'penalties', title: 'Penalties & Interest', slug: 'penalties' },
      ]
    },
    {
      id: 'special-cases',
      title: 'Special Cases',
      icon: '⚡',
      subtopics: [
        { id: 'partial-exemption', title: 'Partial Exemption', slug: 'partial-exemption' },
        { id: 'mixed-supplies', title: 'Mixed Supplies', slug: 'mixed-supplies' },
        { id: 'bad-debt-relief', title: 'Bad Debt Relief', slug: 'bad-debt-relief' },
        { id: 'group-registration', title: 'Group Registration', slug: 'group-registration' },
      ]
    },
  ];

  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => 
      prev.includes(topicId) 
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId]
    );
  };

  const selectArticle = (article) => {
    setSelectedArticle(article);
    // Scroll to top on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredStructure = searchQuery
    ? knowledgeStructure.map(topic => ({
        ...topic,
        subtopics: topic.subtopics.filter(sub =>
          sub.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(topic => topic.subtopics.length > 0)
    : knowledgeStructure;

  // Get article by slug
  const getArticleBySlug = (slug) => {
    return articles.find(article => article.slug === slug);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-red-600 p-2 rounded-lg">
                <Book className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Knowledge Center</h1>
                <p className="text-sm text-slate-600">Swiss VAT guides and documentation</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <a 
                href="/chat" 
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Ask VAT Bot</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Sidebar - Navigation */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 sticky top-24">
              {/* Search */}
              <div className="p-4 border-b border-slate-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search topics..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  />
                </div>
              </div>

              {/* Navigation Tree */}
              <div className="max-h-[calc(100vh-250px)] overflow-y-auto">
                {filteredStructure.map((topic) => (
                  <div key={topic.id} className="border-b border-slate-100 last:border-b-0">
                    <button
                      onClick={() => toggleTopic(topic.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{topic.icon}</span>
                        <span className="font-semibold text-slate-900">{topic.title}</span>
                      </div>
                      {expandedTopics.includes(topic.id) ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                    </button>

                    {expandedTopics.includes(topic.id) && (
                      <div className="bg-slate-50">
                        {topic.subtopics.map((subtopic) => {
                          const article = getArticleBySlug(subtopic.slug);
                          return (
                            <button
                              key={subtopic.id}
                              onClick={() => article && selectArticle(article)}
                              className={`w-full text-left px-4 py-3 pl-12 hover:bg-white transition-colors border-l-2 ${
                                selectedArticle?.slug === subtopic.slug
                                  ? 'border-red-600 bg-white text-red-600 font-medium'
                                  : 'border-transparent text-slate-600'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm">{subtopic.title}</span>
                                {!article && (
                                  <span className="text-xs text-slate-400">Coming soon</span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {selectedArticle ? (
              // Article View
              <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                {/* Article Header */}
                <div className="p-8 border-b border-slate-200">
                  <button
                    onClick={() => setSelectedArticle(null)}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 lg:hidden"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm">Back to topics</span>
                  </button>
                  
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex-1">
                      <h1 className="text-3xl font-bold text-slate-900 mb-2">
                        {selectedArticle.title}
                      </h1>
                      {selectedArticle.description && (
                        <p className="text-lg text-slate-600">
                          {selectedArticle.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                    {selectedArticle.author && (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{selectedArticle.author}</span>
                      </div>
                    )}
                    {selectedArticle.published_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(selectedArticle.published_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {selectedArticle.category && (
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        <span>{selectedArticle.category}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Article Content */}
                <div className="p-8 prose prose-slate max-w-none">
                  <div 
                    dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                    className="article-content"
                  />
                </div>

                {/* Related Articles */}
                {selectedArticle.related_articles && selectedArticle.related_articles.length > 0 && (
                  <div className="p-8 border-t border-slate-200 bg-slate-50">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Related Articles</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedArticle.related_articles.map((relatedSlug) => {
                        const related = getArticleBySlug(relatedSlug);
                        return related ? (
                          <button
                            key={related.slug}
                            onClick={() => selectArticle(related)}
                            className="flex items-center gap-3 p-4 bg-white rounded-lg border border-slate-200 hover:border-red-300 transition-colors text-left"
                          >
                            <FileText className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="font-medium text-slate-900">{related.title}</div>
                              {related.description && (
                                <div className="text-sm text-slate-500 line-clamp-2">{related.description}</div>
                              )}
                            </div>
                          </button>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Welcome View
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
                <div className="text-center max-w-2xl mx-auto">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Book className="w-8 h-8 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">
                    Welcome to the Swiss VAT Knowledge Center
                  </h2>
                  <p className="text-slate-600 mb-8">
                    Comprehensive guides and documentation on Swiss VAT regulations. 
                    Select a topic from the left sidebar to get started, or use the search to find specific information.
                  </p>

                  {/* Quick Start Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                    <div className="p-4 border border-slate-200 rounded-lg hover:border-red-300 transition-colors">
                      <div className="text-2xl mb-2">📚</div>
                      <h3 className="font-semibold text-slate-900 mb-1">Getting Started</h3>
                      <p className="text-sm text-slate-600">Learn VAT basics and registration requirements</p>
                    </div>
                    <div className="p-4 border border-slate-200 rounded-lg hover:border-red-300 transition-colors">
                      <div className="text-2xl mb-2">💰</div>
                      <h3 className="font-semibold text-slate-900 mb-1">Rates & Deductions</h3>
                      <p className="text-sm text-slate-600">Understand different VAT rates and when to apply them</p>
                    </div>
                    <div className="p-4 border border-slate-200 rounded-lg hover:border-red-300 transition-colors">
                      <div className="text-2xl mb-2">🏢</div>
                      <h3 className="font-semibold text-slate-900 mb-1">Industry Guides</h3>
                      <p className="text-sm text-slate-600">Specific guidance for your business type</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CSS for article content */}
      <style jsx global>{`
        .article-content h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: #1e293b;
        }
        .article-content h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #334155;
        }
        .article-content p {
          margin-bottom: 1rem;
          line-height: 1.75;
          color: #475569;
        }
        .article-content ul, .article-content ol {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }
        .article-content li {
          margin-bottom: 0.5rem;
        }
        .article-content a {
          color: #dc2626;
          text-decoration: underline;
        }
        .article-content code {
          background: #f1f5f9;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-size: 0.875em;
        }
        .article-content pre {
          background: #1e293b;
          color: #f1f5f9;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin-bottom: 1rem;
        }
        .article-content blockquote {
          border-left: 4px solid #dc2626;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #64748b;
          font-style: italic;
        }
        .article-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }
        .article-content th, .article-content td {
          border: 1px solid #e2e8f0;
          padding: 0.75rem;
          text-align: left;
        }
        .article-content th {
          background: #f8fafc;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default KnowledgeCenter;
